import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ChevronDown = () => (
  <svg className="w-4 h-4 pointer-events-none shrink-0" style={{ color: '#4A4742' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const MENU_MAX_HEIGHT = 240; // matches max-h-60
const DEFAULT_BUTTON_CLASS = 'w-full h-11 pl-4 pr-3 border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:border-[#3F707D] bg-white disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between cursor-pointer';

// Custom-styled dropdown that replaces the native <select> options list, whose
// appearance is controlled by the OS and can't be themed to match our UI.
// Pass `multiple` to let users pick more than one option; `value`/`onChange`
// then operate on an array instead of a single value.
//
// For paginated data sources, pass `onLoadMore`/`hasMore`/`loadingMore` — the
// menu fetches the next page itself when the user scrolls near the bottom.
export const CustomSelect = ({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  buttonClassName = DEFAULT_BUTTON_CLASS,
  multiple = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  searchable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [search, setSearch] = useState('');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const visibleOptions = searchable && search.trim()
    ? options.filter(o => o.label?.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : null;
  const selected = multiple ? null : options.find(o => o.value === value);
  const isSelected = (v) => (multiple ? selectedValues.includes(v) : value === v);
  const hasSelection = multiple ? selectedValues.length > 0 : !!selected;

  // The menu is rendered in a portal with fixed positioning (see render below)
  // so a scrollable ancestor (e.g. a modal body) can never clip it — position
  // is computed purely from the button's viewport position. Open upward only
  // when there genuinely isn't enough room below.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4, maxHeight: Math.max(80, Math.min(MENU_MAX_HEIGHT, spaceAbove - 8)) }
        : { top: rect.bottom + 4, maxHeight: Math.max(80, Math.min(MENU_MAX_HEIGHT, spaceBelow - 8)) }),
    });
  }, [open]);

  // Reposition/close is only meaningful while the menu is visible — a scroll
  // anywhere (including inside a modal body) can move the button, so just
  // close the menu rather than risk it floating over the wrong spot.
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    if (searchable) buttonRef.current?.focus();
  }, [open, searchable]);

  const handlePick = (v) => {
    if (!multiple) {
      onChange(v);
      setOpen(false);
      return;
    }
    onChange(
      selectedValues.includes(v)
        ? selectedValues.filter(x => x !== v)
        : [...selectedValues, v]
    );
  };

  const displayText = multiple
    ? (selectedValues.length > 0
        ? options.filter(o => selectedValues.includes(o.value)).map(o => o.label).join(', ')
        : placeholder)
    : (selected ? selected.label : placeholder);

  // Fetch the next page a little before the user hits the physical bottom,
  // so the list keeps feeling continuous instead of stalling on each page edge.
  const SCROLL_LOAD_THRESHOLD = 48;
  const handleMenuScroll = (e) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD) {
      onLoadMore();
    }
  };

  const showSearchInput = searchable && open && !disabled;

  return (
    <div className={`relative ${className}`}>
      {showSearchInput ? (
        <input
          ref={buttonRef}
          type="text"
          id={id}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={() => setOpen(true)}
          placeholder={displayText}
          className={buttonClassName}
        />
      ) : (
        <button
          ref={buttonRef}
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen(v => !v)}
          className={buttonClassName}
        >
          <span className={`min-w-0 truncate ${hasSelection ? 'text-gray-700' : 'text-gray-400'}`}>{displayText}</span>
          <ChevronDown />
        </button>
      )}

      {open && !disabled && menuStyle && createPortal(
        <>
          <div className="fixed inset-0 z-[999]" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            style={{ ...menuStyle, display: 'flex', flexDirection: 'column' }}
            className="z-[1000] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div className="py-1 overflow-auto" onScroll={handleMenuScroll}>
              {visibleOptions.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-400">
                  {loadingMore ? 'Loading...' : 'No options available'}
                </p>
              ) : visibleOptions.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handlePick(o.value)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2 ${isSelected(o.value) ? 'text-[#3F707D] font-medium' : 'text-gray-700'}`}
                >
                  {multiple && (
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected(o.value) ? 'bg-[#3F707D] border-[#3F707D]' : 'border-gray-300'}`}>
                      {isSelected(o.value) && <CheckIcon />}
                    </span>
                  )}
                  <span className="min-w-0 truncate">{o.label}</span>
                </button>
              ))}
              {visibleOptions.length > 0 && loadingMore && (
                <p className="px-3 py-2 text-xs text-gray-400">Loading more...</p>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
